import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from '../../../shared/base-classes/base.component';
import {
  HeroEventCreateDto,
  HeroEventRecurrenceEnum,
  HeroEventResponseDto,
  HeroEventUpdateDto,
  IDisplayData,
  StudentBasicListResponseDto
} from '@whetstoneeducation/hero-common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppToastManagerService } from '../../../shared/services/toast-manager.service';
import { AppPageHeaderService } from '../../../shared/page-header/page-header.service';
import { AppEventService } from '../events.service';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { HeaderButtonAction } from '../../../shared/page-header/header-button';
import {
  dtoToFormGroup,
  formCanSave,
  validateAndGetValue
} from '../../../shared/validation/validation.util';
import { IHeroCreateUpdateResolverData } from './hero-event-create-edit.resolver';
import { MatDialog } from '@angular/material/dialog';
import { AppHeroEventCreateEditAddStudentComponent } from './hero-event-create-edit-add-student/hero-event-create-edit-add-student.component';
import { BulkEventStudentCsvComponent } from "./bulk-event-student-csv/bulk-event-student-csv.component";

@Component({
  selector: 'app-hero-event-create-edit',
  templateUrl: './hero-event-create-edit.component.html',
  styleUrls: ['./hero-event-create-edit.component.scss']
})
export class AppHeroEventCreateEditComponent
  extends BaseComponent
  implements OnInit, OnDestroy {
  public isLoading = true;
  public students: StudentBasicListResponseDto[];
  public id: number;
  public heroEventForm: FormGroup;
  public heroEventCategoryOptions: IDisplayData[];
  public recurrenceOptions: IDisplayData[] = [
    {
      value: HeroEventRecurrenceEnum.ONE_TIME,
      display: 'one time'
    },
    {
      value: HeroEventRecurrenceEnum.DAILY,
      display: 'daily'
    },
    {
      value: HeroEventRecurrenceEnum.WEEKLY,
      display: 'weekly'
    },
    {
      value: HeroEventRecurrenceEnum.MONTHLY,
      display: 'monthly'
    }
  ];
  public dayOptions: IDisplayData[] = [
    { display: 'Sunday', value: 0 },
    { display: 'Monday', value: 1 },
    { display: 'Tuesday', value: 2 },
    { display: 'Wednesday', value: 3 },
    { display: 'Thursday', value: 4 },
    { display: 'Friday', value: 5 },
    { display: 'Saturday', value: 6 }
  ];
  public monthlyRecurrenceOptions: IDisplayData[] = [
    { display: 'First of the month', value: 1 },
    { display: 'Second of the month', value: 2 },
    { display: 'Third of the month', value: 3 },
    { display: 'Last of the month', value: 4 }
  ];
  public timezone: string;

  public pageHeaderSubscription: Subscription;

  public filteredCategoryOptions: Observable<IDisplayData[]>;
  public filteredRecurrenceOptions: Observable<IDisplayData[]>;

  constructor(
    public route: ActivatedRoute,
    public formBuilder: FormBuilder,
    public toastManager: AppToastManagerService,
    public router: Router,
    private pageHeaderService: AppPageHeaderService,
    public eventsService: AppEventService,
    public dialog: MatDialog
  ) {
    super();
    this.setupHeaderButtonSubscriptions();
    const user = this.StorageManager.getLoggedInUser();
    this.timezone = user.settings.timezone;
  }

  ngOnDestroy() {
    this.pageHeaderSubscription.unsubscribe();
  }

  async ngOnInit() {
    const { heroEvent, eventCategories } = this.route.snapshot.data
      .data as IHeroCreateUpdateResolverData;

    this.heroEventCategoryOptions = eventCategories;
    if (heroEvent.id) {
      this.id = heroEvent.id;
      const updateDto = new HeroEventUpdateDto();
      updateDto.mapFields(heroEvent);
      updateDto.recurrenceScheduledDays = updateDto.recurrenceScheduledDays.map(
        (day: any) => parseInt(day)
      );
      if (updateDto.monthlyRecurrence) {
        updateDto.monthlyRecurrence = updateDto.monthlyRecurrence.map(
          (month: any) => parseInt(month)
        );
      }
      this.heroEventForm = dtoToFormGroup(updateDto, this.formBuilder, { updateOn: 'change' });
      this.students = heroEvent.students;
    } else {
      const createDto = new HeroEventCreateDto();
      createDto.mapFields(heroEvent);
      this.heroEventForm = dtoToFormGroup(createDto, this.formBuilder, { updateOn: 'change' });
    }

    this.filteredCategoryOptions = this.heroEventForm.get('eventCategoryId').valueChanges.pipe(
      startWith(''),
      map((value) => {
        const label = typeof value === 'string' ? value : value?.display;
        return this._filter(label, 'heroEventCategoryOptions');
      })
    )

    this.filteredRecurrenceOptions = this.heroEventForm.get('recurrence').valueChanges.pipe(
      startWith(''),
      map((value) => {
        const label = typeof value === 'string' ? value : value?.display;
        return this._filter(label, 'recurrenceOptions');
      })
    )

    this.isLoading = false;
  }

  private _filter(value: string, options: string): IDisplayData[] {
    if (!this[options]) {
      return [];
    } else if (!value) {
      return this[options];
    }

    const filterValue = value?.toLowerCase();

    return this[options].filter((option) => option.display.toLowerCase().includes(filterValue));
  }

  displaySelectedCategory(option: IDisplayData | null): string {
    const groupSelected = this.heroEventCategoryOptions.find(item => item.value === option);
    return groupSelected?.display || '';
  }

  displaySelectedRecurrence(option: IDisplayData | null): string {
    const groupSelected = this.recurrenceOptions.find(item => item.value === option);
    return groupSelected?.display || '';
  }

  public handleTimeChange(event: any) {
    this.heroEventForm.controls['startTime'].setValue(event.target.value);
  }

  public setupHeaderButtonSubscriptions() {
    this.pageHeaderSubscription =
      this.pageHeaderService.buttonAction$.subscribe(
        async (action: HeaderButtonAction) => {
          this.isLoading = true;

          if (action === HeaderButtonAction.BACK) {
            await this.router.navigate(['events']);
          }

          if (
            action === HeaderButtonAction.SAVE &&
            formCanSave(this.heroEventForm, this.toastManager, true)
          ) {
            try {
              if (!this.id) {
                const createHeroEventDto =
                  await validateAndGetValue<HeroEventCreateDto>(
                    this.heroEventForm,
                    HeroEventCreateDto
                  );

                const heroEvent =
                  await this.eventsService.createHeroEvent(createHeroEventDto);
                await this.router.navigate(['event', heroEvent.id]);
              } else {
                const updateHeroEventDto =
                  await validateAndGetValue<HeroEventUpdateDto>(
                    this.heroEventForm,
                    HeroEventUpdateDto
                  );

                const heroEvent = await this.eventsService.updateHeroEvent(
                  this.id,
                  updateHeroEventDto
                );
                this.reloadUpdateDto(heroEvent);
              }
            } catch (error) {
              this.toastManager.error('error creating event');
            }
          }
          this.isLoading = false;
        }
      );
  }

  public async removeStudent(studentId: number) {
    this.isLoading = true;
    await this.eventsService.deleteHeroEventStudent(this.id, studentId);
    const heroEvent = await this.eventsService.getHeroEvent(this.id);
    this.reloadUpdateDto(heroEvent);
    this.isLoading = false;
  }

  public reloadUpdateDto(heroEvent: HeroEventResponseDto) {
    const updateDto = new HeroEventUpdateDto();
    updateDto.mapFields(heroEvent);
    updateDto.recurrenceScheduledDays = updateDto.recurrenceScheduledDays.map(
      (day: any) => parseInt(day)
    );
    this.heroEventForm = dtoToFormGroup(updateDto, this.formBuilder);
    this.students = heroEvent.students;
  }

  public showAddStudentModal() {
    this.dialog.open(AppHeroEventCreateEditAddStudentComponent, {
      width: '800px',
      height: '600px',
      panelClass: 'default-dialog',
      data: {
        heroEventId: this.id,
        onClose: async () => {
          this.isLoading = true;
          const heroEvent = await this.eventsService.getHeroEvent(this.id);
          this.reloadUpdateDto(heroEvent);
          this.isLoading = false;
        }
      }
    });
  }

  public showBulkStudentProfileUploadModal() {
    this.dialog.open(BulkEventStudentCsvComponent, {
      width: '600px',
      height: 'auto',
      panelClass: 'default-dialog',
      data: {
        heroEventId: this.id,
        onClose: async () => {
          this.isLoading = true;
          const heroEvent = await this.eventsService.getHeroEvent(this.id);
          this.reloadUpdateDto(heroEvent);
          this.isLoading = false;
        }
      }
    });
  }

  public onClear(control: string) {
    this.heroEventForm.get(control).setValue(undefined);
  }

  protected readonly HeroEventRecurrenceEnum = HeroEventRecurrenceEnum;
}
